/*
  Shipping Protection switch (our replacement for the Shipcheck app; markup in snippets/ri-shipping-protection.liquid).

  Off by default. Turning it on adds one line of the product shipping-protection-ri, the variant for this cart:
  $1 for every $20 of items that ship, up to $150 at $3,000. While it is on, any change to the cart (an add on a
  shelf, a quantity change in Your Cart or on the cart page) swaps the line to the right tier, and a cart with
  nothing left that ships drops it. Turning it off removes the line.

  The cart can change from many places, so the script listens to all of them: Dawn's cart-update event, the
  header cart total changing, and Your Cart repainting (ri-added-panel.js calls RIProtection.onCart).
*/
(function () {
  'use strict';

  var HANDLE = 'shipping-protection-ri';
  var TYPE = 'Shipping Protection';
  var STEP = 2000;
  var TOP_TIER = 150;
  var tiers = null;
  var tiersLoading = null;
  var busy = false;
  var timer = 0;

  /* Dawn declares PUB_SUB_EVENTS with const (constants.js), so it is a shared global but not on window. */
  function pubSubEvents() {
    return typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null;
  }

  function route(name, fallback) {
    return (window.routes && window.routes[name]) || fallback;
  }

  function json(url, body) {
    var options = { credentials: 'same-origin', headers: { Accept: 'application/json' } };
    if (body) {
      options.method = 'POST';
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }
    return fetch(url, options).then(function (response) {
      return response.json();
    });
  }

  function getCart() {
    return json(route('cart_url', '/cart') + '.js');
  }

  function isOurs(line) {
    return line && line.handle === HANDLE;
  }

  function isProtection(line) {
    return isOurs(line) || (line && line.product_type === TYPE);
  }

  function shippingCents(cart) {
    return (cart.items || []).reduce(function (sum, line) {
      if (isProtection(line) || !line.requires_shipping) return sum;
      return sum + (line.final_line_price || 0);
    }, 0);
  }

  function tierFor(cents) {
    if (cents <= 0) return 0;
    return Math.min(TOP_TIER, Math.ceil(cents / STEP));
  }

  /* Variant id for each tier, read once from the product (the variant priced $N is tier N). */
  function loadTiers() {
    if (tiers) return Promise.resolve(tiers);
    if (!tiersLoading) {
      tiersLoading = json('/products/' + HANDLE + '.js').then(function (product) {
        var map = {};
        (product.variants || []).forEach(function (variant) {
          map[Math.round(variant.price / 100)] = variant.id;
        });
        tiers = map;
        return map;
      });
      tiersLoading.catch(function () {
        tiersLoading = null;
      });
    }
    return tiersLoading;
  }

  function ourLines(cart) {
    return (cart.items || []).filter(isOurs);
  }

  function money(cents) {
    if (window.RIMoney && window.RIMoney.formatCents) return window.RIMoney.formatCents(cents, '$');
    return '$' + (cents / 100).toFixed(2);
  }

  function paint(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    var tier = tierFor(shippingCents(cart));
    var on = ourLines(cart).length > 0;
    document.querySelectorAll('[data-ri-protect]').forEach(function (row) {
      row.hidden = tier === 0;
      row.classList.toggle('is-on', on);
      var price = row.querySelector('[data-ri-protect-price]');
      if (price && tier) price.innerHTML = money(tier * 100);
      var toggle = row.querySelector('[data-ri-protect-switch]');
      if (toggle) {
        toggle.setAttribute('aria-checked', on ? 'true' : 'false');
        toggle.setAttribute('aria-label', 'Shipping Protection, $' + tier + '.00');
        toggle.removeAttribute('aria-busy');
      }
    });
  }

  /* Changes that bring the line in line with the cart: the right tier, quantity 1, gone when nothing ships. */
  function fixes(cart, map) {
    var lines = ourLines(cart);
    if (!lines.length) return null;
    var tier = tierFor(shippingCents(cart));
    var want = tier ? map[tier] : null;
    var updates = {};
    var changed = false;
    lines.forEach(function (line) {
      if (line.variant_id !== want) {
        updates[line.variant_id] = 0;
        changed = true;
      } else if (line.quantity !== 1) {
        updates[line.variant_id] = 1;
        changed = true;
      }
    });
    if (want && !lines.some(function (line) { return line.variant_id === want; })) {
      updates[want] = 1;
      changed = true;
    }
    return changed ? updates : null;
  }

  function applyUpdates(updates) {
    return json(route('cart_update_url', '/cart/update') + '.js', { updates: updates });
  }

  /* Redraw everything that shows the cart after this script changed it. */
  function redraw(cart) {
    paint(cart);
    var slot = document.querySelector('#cart-icon-bubble .ri-header__cart-total');
    if (slot && cart.item_count) slot.innerHTML = money(cart.total_price);
    if (typeof window.RIAddedPanelRefresh === 'function') window.RIAddedPanelRefresh();
    redrawCartPage(cart);
    var events = pubSubEvents();
    if (events && typeof publish === 'function') publish(events.cartUpdate, { source: 'ri-protection', cartData: cart });
  }

  /* Cart page: Dawn's own refresh redraws the items but not the totals, so redraw both here. */
  function redrawCartPage(cart) {
    var items = document.getElementById('main-cart-items');
    var footer = document.getElementById('main-cart-footer');
    if (!items || !footer) return;
    /* Dropping the line emptied the cart (its last game was just removed): the page's empty layout is a
       different template state, so load it fresh instead of patching. */
    if (cart && !cart.item_count) {
      window.location.reload();
      return;
    }
    var ids = [items.dataset.id, footer.dataset.id];
    /* A plain request: with Accept: application/json the cart route answers with something else, not the sections. */
    fetch(route('cart_url', '/cart') + '?sections=' + ids.join(',') + '&_=' + Date.now(), { credentials: 'same-origin' })
      .then(function (response) {
        return response.json();
      })
      .then(function (sections) {
        [[items, ids[0]], [footer, ids[1]]].forEach(function (pair) {
          var html = sections && sections[pair[1]];
          if (!html) return;
          var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.js-contents');
          var target = pair[0].querySelector('.js-contents');
          if (fresh && target) target.innerHTML = fresh.innerHTML;
        });
      })
      .catch(function () {});
  }

  /* Bring the line in step with a cart we already have; resolves with the cart as it now is. */
  function sync(cart) {
    if (!ourLines(cart).length) {
      paint(cart);
      return Promise.resolve(cart);
    }
    return loadTiers().then(function (map) {
      var updates = fixes(cart, map);
      if (!updates) {
        paint(cart);
        return cart;
      }
      return applyUpdates(updates).then(function () {
        return getCart();
      }).then(function (fresh) {
        redraw(fresh);
        return fresh;
      });
    });
  }

  function schedule() {
    window.clearTimeout(timer);
    timer = window.setTimeout(function () {
      if (busy) return schedule();
      busy = true;
      getCart().then(sync).catch(function () {}).then(function () {
        busy = false;
      });
    }, 450);
  }

  function toggle(button) {
    if (busy) return;
    busy = true;
    var turningOn = button.getAttribute('aria-checked') !== 'true';
    button.setAttribute('aria-checked', turningOn ? 'true' : 'false');
    button.setAttribute('aria-busy', 'true');
    /* The protected / not protected line follows the switch at once; paint() confirms it from the cart. */
    var row = button.closest('[data-ri-protect]');
    if (row) row.classList.toggle('is-on', turningOn);
    getCart()
      .then(function (cart) {
        if (!turningOn) {
          var updates = {};
          (cart.items || []).forEach(function (line) {
            if (isOurs(line)) updates[line.variant_id] = 0;
          });
          return applyUpdates(updates);
        }
        return loadTiers().then(function (map) {
          var id = map[tierFor(shippingCents(cart))];
          if (!id) return null;
          return json(route('cart_add_url', '/cart/add') + '.js', { items: [{ id: id, quantity: 1 }] });
        });
      })
      .then(function () {
        return getCart();
      })
      .then(function (cart) {
        busy = false;
        redraw(cart);
      })
      .catch(function () {
        busy = false;
        getCart().then(paint).catch(function () {});
      });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-ri-protect-switch]');
    if (!button) return;
    event.preventDefault();
    toggle(button);
  });

  window.RIProtection = {
    /* Your Cart passes every cart it paints. */
    onCart: function (cart) {
      paint(cart);
      if (ourLines(cart || {}).length) schedule();
    },
    isProtection: isProtection,
    tierFor: tierFor,
    shippingCents: shippingCents
  };

  function start() {
    var events = pubSubEvents();
    if (events && typeof subscribe === 'function') {
      subscribe(events.cartUpdate, function (event) {
        if (event && event.source === 'ri-protection') return;
        schedule();
      });
    }
    /* Shelf adds and Your Cart change the header total; that is the one place every cart change shows. */
    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble && window.MutationObserver) {
      new MutationObserver(schedule).observe(bubble, { childList: true, subtree: true, characterData: true });
    }
    if (document.querySelector('[data-ri-protect]')) schedule();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
